<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'

// 页面配置
definePage({
  name: 'InstitutionNumber',
  meta: {
    title: '机构号',
  },
})

// 搜索相关
const searchQuery = ref('')
const filteredData = ref<any[]>([])

// 机构数据（模拟真实数据）
const institutionData = reactive([
  { name: 'TradeGo Markets', code: 'TGM001', type: '券商', status: '已认证', isHot: true },
  { name: 'Alpha Capital', code: 'AC002', type: '基金公司', status: '已认证', isHot: false },
  { name: 'Beta Investment', code: 'BI003', type: '私募', status: '待审核', isHot: true },
  { name: 'Gamma Finance', code: 'GF004', type: '银行', status: '已认证', isHot: false },
  { name: 'Delta Wealth', code: 'DW005', type: '信托', status: '已认证', isHot: true },
  { name: 'Epsilon Trading', code: 'ET006', type: '券商', status: '已认证', isHot: false },
  { name: 'Zeta Capital', code: 'ZC007', type: '基金公司', status: '已认证', isHot: true },
  { name: 'Theta Investment', code: 'TI008', type: '私募', status: '已认证', isHot: false },
  { name: 'Iota Finance', code: 'IF009', type: '银行', status: '待审核', isHot: true },
  { name: 'Kappa Wealth', code: 'KW010', type: '信托', status: '已认证', isHot: false },
  { name: 'Lambda Trading', code: 'LT011', type: '券商', status: '已认证', isHot: true },
  { name: 'Mu Capital', code: 'MC012', type: '基金公司', status: '已认证', isHot: false },
  { name: 'Nu Investment', code: 'NI013', type: '私募', status: '待审核', isHot: true },
  { name: 'Xi Finance', code: 'XF014', type: '银行', status: '已认证', isHot: false },
  { name: 'Omicron Wealth', code: 'OW015', type: '信托', status: '已认证', isHot: true },
  { name: 'Pi Trading', code: 'PT016', type: '券商', status: '已认证', isHot: false },
  { name: 'Rho Capital', code: 'RC017', type: '基金公司', status: '已认证', isHot: true },
  { name: 'Sigma Investment', code: 'SI018', type: '私募', status: '已认证', isHot: false },
  { name: 'Tau Finance', code: 'TF019', type: '银行', status: '待审核', isHot: true },
  { name: 'Upsilon Wealth', code: 'UW020', type: '信托', status: '已认证', isHot: false },
  { name: 'Phi Trading', code: 'PT021', type: '券商', status: '已认证', isHot: true },
  { name: 'Chi Capital', code: 'CC022', type: '基金公司', status: '已认证', isHot: false },
  { name: 'Psi Investment', code: 'PI023', type: '私募', status: '已认证', isHot: true },
  { name: 'Omega Finance', code: 'OF024', type: '银行', status: '已认证', isHot: false },
])

// 索引列表 A-Z
const indexList = ref<string[]>([])
for (let i = 0; i < 26; i++) {
  indexList.value.push(String.fromCharCode(65 + i)) // A-Z
}

// 过滤数据
function filterData() {
  if (!searchQuery.value) {
    filteredData.value = [...institutionData]
    return
  }

  const query = searchQuery.value.toLowerCase()
  filteredData.value = institutionData.filter(item =>
    item.name.toLowerCase().includes(query)
    || item.code.toLowerCase().includes(query),
  )
}
function handleItemClick(item: any) {
  console.warn('点击了机构：', item)
}

// 监听搜索框变化
watch(searchQuery, filterData)

// 初始化数据
onMounted(() => {
  filteredData.value = [...institutionData]
})

// 计算属性：按字母分组的数据
const groupedData = computed(() => {
  const result: Record<string, any[]> = {}

  filteredData.value.forEach((item) => {
    const firstLetter = item.name.charAt(0).toUpperCase()
    if (!result[firstLetter]) {
      result[firstLetter] = []
    }
    result[firstLetter].push(item)
  })

  return result
})
</script>

<template>
  <FmPageLayout navbar :search="false" back>
    <div class="fund-flow-page">
      <!-- 搜索框 -->
      <div class="search-container">
        <van-search
          v-model="searchQuery" placeholder="搜索机构名称/中央代码" :clearable="true" class="border-b-[#e4e5e9] rounded-8px p-0"
          @search="filterData" @cancel="searchQuery = ''"
        >
          <template #left-icon>
            <!-- <FmIcon name="tdesign:search" class="color-[#CCCCCC]" /> -->
            <img src="@/assets/images/search.png" alt="" width="14px" class="m-r-9px">
          </template>
        </van-search>
      </div>

      <!-- 机构列表 -->
      <div class="institution-list">
        <van-index-bar :index-list="indexList">
          <!-- A-Z 索引锚点 -->
          <template v-for="letter in indexList" :key="letter">
            <van-index-anchor :index="letter" class="h-0" />

            <!-- 根据字母分组的数据 -->
            <van-cell
              v-for="item in groupedData[letter]" :key="item.code" :title="item.name" is-link
              @click="handleItemClick(item)"
            >
              <!-- :label="`代码：${item.code} `" -->
              <template #icon>
                <div class="icon-container">
                  <div v-if="item.isHot" class="triangle-badge">
                    热门
                  </div>
                  <!-- 默认灰色头像 -->
                  <div class="avatar">
                    T
                  </div>
                </div>
              </template>
              <template #title>
                <div class="text-14px line-height-normal">
                  <div>{{ item.name }}</div>
                  <div class="text-10px">
                    {{ item.code }}
                  </div>
                </div>
              </template>
              <template #right-icon>
                <div class="btnbox">
                  <van-button size="small" type="primary" round plain style="width: 72px;padding: 2px 10px;">
                    + 关注
                  </van-button>
                </div>
              </template>
            </van-cell>
          </template>
        </van-index-bar>
      </div>
    </div>
  </FmPageLayout>
</template>

<style scoped lang="scss">
.fund-flow-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0;
  color: white;
  background-color: #007aff;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
}

.back-btn {
  padding: 8px;
  color: white;
  cursor: pointer;
  background: none;
  border: none;
}

.search-container {
  padding: 8px;
  background-color: white;
  // border-bottom: 1px solid #e4e5e9;
  :deep(input)::placeholder {
    color: #636367;
  }
}

.institution-list {
  // margin-top: 16px;
  background-color: white;
}

.van-index-bar {
  background-color: white;

  .van-cell {
    padding: 10px 20px;
    margin-left: 9px;
    border-bottom: 0.5px solid #e4e5e9;
  }

  .van-cell:last-child {
    border-bottom: none;
  }

  :deep(.van-cell:first-child) {
    border-top: 0.5px solid #e4e5e9;
  }
}

.van-index-anchor {
  padding: 8px 16px;
  font-size: 14px;
  color: #999;
}

.van-cell__title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.van-cell__label {
  font-size: 12px;
  color: #666;
}

.van-button {
  padding: 4px 8px;
  font-size: 12px;
}

.hot-tag {
  width: 32px;
  height: 16px;
  margin-right: 8px;
  font-size: 10px;
  line-height: 16px;
  color: white;
  text-align: center;
  background-color: #ff6b00;
  border-radius: 2px;
}

.icon-container {
  width: 60px;
}

.btnbox {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100px;
}

.triangle-badge {
  position: absolute;
  top: -25px;
  left: -25px;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 50px;
  height: 50px;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
  color: white;
  background-color: #ff6b00;
  transform: rotate(-45deg);
}

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: 8px;
  font-size: 14px;
  color: #999;
  background-color: #ddd;
  border-radius: 50%;
}
</style>
